import {
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useActiveOrgSlug } from '@/lib/activeOrg';
import { useMe } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { formatInventoryForm } from '@/lib/inventory';
import {
  type Location,
  type LocationPartRow,
  useCreateLocation,
  useDeleteLocation,
  useDeleteLocationImage,
  useLocationParts,
  useLocations,
  useUploadLocationImage,
} from '@/lib/locations';

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';

interface TreeNode {
  loc: Location;
  children: TreeNode[];
  depth: number;
}

/**
 * Group flat locations into a parent/child tree. The schema only supports
 * one level of nesting today (Location.parent_location_id is a single
 * self-FK with no path), but we render recursively anyway so a future
 * deeper tree wouldn't need a UI rewrite.
 *
 * Top-level: anything whose parent isn't in the visible set (covers
 * both `parent_location_id === null` and dangling parents from race
 * conditions). Sorted alphabetically at every depth.
 */
function buildTree(locations: Location[]): TreeNode[] {
  const ids = new Set(locations.map((l) => l.id));
  const byParent = new Map<number | null, Location[]>();
  for (const loc of locations) {
    const parentKey =
      loc.parent_location_id !== null && ids.has(loc.parent_location_id)
        ? loc.parent_location_id
        : null;
    const list = byParent.get(parentKey) ?? [];
    list.push(loc);
    byParent.set(parentKey, list);
  }

  const sortAlpha = (a: Location, b: Location) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

  function build(parentId: number | null, depth: number): TreeNode[] {
    const direct = byParent.get(parentId) ?? [];
    direct.sort(sortAlpha);
    return direct.map((loc) => ({
      loc,
      depth,
      children: build(loc.id, depth + 1),
    }));
  }

  return build(null, 0);
}

function formatCents(cents: number): string {
  if (!cents) return '—';
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function LocationsPage() {
  const me = useMe();
  const orgSlug = useActiveOrgSlug(me.data?.organizations);

  const list = useLocations(orgSlug);
  const create = useCreateLocation(orgSlug);
  const remove = useDeleteLocation(orgSlug);

  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<string>('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const tree = useMemo(() => buildTree(list.data ?? []), [list.data]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim()) return;
    try {
      await create.mutateAsync({
        name: name.trim(),
        parent_location_id: parentId === '' ? null : Number(parentId),
      });
      setName('');
      setParentId('');
    } catch {
      /* surfaced below */
    }
  };

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Locations</h1>
        <p className="mt-1 text-stone-600 dark:text-stone-400">
          Where parts physically live — bins, drawers, reels. Click a row
          to see what's inside.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="flex flex-wrap items-end gap-2 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-4"
      >
        <label className="min-w-[180px] flex-1">
          <span className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
            Name
          </span>
          <Input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Bench Drawer A"
          />
        </label>
        <label className="min-w-[180px]">
          <span className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
            Parent (optional)
          </span>
          <select
            className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
          >
            <option value="">— none —</option>
            {list.data?.map((loc) => (
              <option key={loc.id} value={String(loc.id)}>
                {loc.name}
              </option>
            ))}
          </select>
        </label>
        <Button type="submit" disabled={create.isPending || !name.trim()}>
          {create.isPending ? 'Adding…' : 'Add location'}
        </Button>
        {create.isError && (
          <p className="basis-full text-sm text-red-600">
            {(create.error as Error).message}
          </p>
        )}
      </form>

      <div className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 bg-stone-50 dark:bg-stone-900 px-3 py-2 text-xs uppercase tracking-wide text-stone-500 dark:text-stone-400">
          <div>Name</div>
          <div className="text-right">Parts</div>
          <div className="text-right">Units</div>
          <div className="text-right">Value</div>
          <div></div>
        </div>

        {list.isPending && (
          <div className="px-3 py-8 text-center text-stone-400 dark:text-stone-500">
            Loading…
          </div>
        )}
        {list.data && list.data.length === 0 && (
          <div className="px-3 py-8 text-center text-stone-500 dark:text-stone-400">
            No locations yet.
          </div>
        )}

        <ul>
          {tree.map((node) => (
            <LocationTreeNode
              key={node.loc.id}
              node={node}
              orgSlug={orgSlug}
              expanded={expanded}
              onToggle={toggle}
              onDelete={(id) => remove.mutate(id)}
              deleting={remove.isPending}
            />
          ))}
        </ul>
      </div>
      {remove.isError && (
        <p className="text-sm text-red-600">
          {(remove.error as Error).message}
        </p>
      )}
    </div>
  );
}

function LocationTreeNode({
  node,
  orgSlug,
  expanded,
  onToggle,
  onDelete,
  deleting,
}: {
  node: TreeNode;
  orgSlug: string | undefined;
  expanded: Set<number>;
  onToggle: (id: number) => void;
  onDelete: (id: number) => void;
  deleting: boolean;
}) {
  const { loc, children, depth } = node;
  const isOpen = expanded.has(loc.id);
  const isPopulated = loc.part_count > 0;

  return (
    <li className="border-t border-stone-100 dark:border-stone-800 first:border-t-0">
      <div
        role="button"
        tabIndex={0}
        onClick={() => onToggle(loc.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(loc.id);
          }
        }}
        className={cn(
          'group grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-center px-3 py-2 cursor-pointer',
          'hover:bg-stone-50 dark:hover:bg-stone-800/50',
        )}
      >
        <div
          className="flex items-center gap-2 min-w-0"
          style={{ paddingLeft: `${depth * 1.5}rem` }}
        >
          <span
            className="text-stone-400 dark:text-stone-500 select-none"
            aria-hidden
          >
            {isOpen ? '▾' : '▸'}
          </span>
          <span
            className={cn(
              'h-2 w-2 rounded-full shrink-0',
              isPopulated ? 'bg-forge-500' : 'bg-stone-200 dark:bg-stone-700',
            )}
            aria-hidden
          />
          <LocationThumb url={loc.image_url} alt={loc.name} />
          <span className="truncate font-medium">{loc.name}</span>
        </div>
        <div
          className={cn(
            'text-right font-mono text-sm',
            isPopulated
              ? 'text-stone-700 dark:text-stone-200'
              : 'text-stone-400 dark:text-stone-600',
          )}
        >
          {loc.part_count}
        </div>
        <div
          className={cn(
            'text-right font-mono text-sm',
            isPopulated
              ? 'text-stone-700 dark:text-stone-200'
              : 'text-stone-400 dark:text-stone-600',
          )}
        >
          {loc.units_on_hand || '—'}
        </div>
        <div
          className={cn(
            'text-right font-mono text-sm',
            loc.value_cents
              ? 'text-stone-700 dark:text-stone-200'
              : 'text-stone-400 dark:text-stone-600',
          )}
        >
          {formatCents(loc.value_cents)}
        </div>
        <div className="text-right">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(loc.id);
            }}
            disabled={loc.part_count > 0 || deleting}
            className="text-xs text-stone-400 dark:text-stone-500 opacity-0 group-hover:opacity-100 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30 transition-opacity"
            title={
              loc.part_count > 0
                ? `In use by ${loc.part_count} parts — reassign first`
                : 'Delete'
            }
          >
            Delete
          </button>
        </div>
      </div>

      {isOpen && (
        <LocationDetailPanel
          location={loc}
          orgSlug={orgSlug}
          depth={depth}
        />
      )}

      {children.length > 0 && (
        <ul>
          {children.map((child) => (
            <LocationTreeNode
              key={child.loc.id}
              node={child}
              orgSlug={orgSlug}
              expanded={expanded}
              onToggle={onToggle}
              onDelete={onDelete}
              deleting={deleting}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function LocationThumb({
  url,
  alt,
}: {
  url: string | null;
  alt: string;
}) {
  if (!url) {
    return (
      <span
        className="h-8 w-8 shrink-0 rounded-md bg-stone-100 dark:bg-stone-800 flex items-center justify-center text-stone-400 dark:text-stone-600"
        aria-hidden
      >
        📦
      </span>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      className="h-8 w-8 shrink-0 rounded-md object-cover bg-stone-100 dark:bg-stone-800"
      loading="lazy"
    />
  );
}

function LocationDetailPanel({
  location,
  orgSlug,
  depth,
}: {
  location: Location;
  orgSlug: string | undefined;
  depth: number;
}) {
  return (
    <div
      className="bg-stone-50 dark:bg-stone-900/50 border-t border-stone-100 dark:border-stone-800 px-3 py-4 space-y-4"
      style={{ paddingLeft: `${depth * 1.5 + 2.25}rem` }}
    >
      <LocationImagePanel location={location} orgSlug={orgSlug} />
      <LocationPartsList
        orgSlug={orgSlug}
        locationId={location.id}
      />
    </div>
  );
}

function LocationImagePanel({
  location,
  orgSlug,
}: {
  location: Location;
  orgSlug: string | undefined;
}) {
  const upload = useUploadLocationImage(orgSlug);
  const remove = useDeleteLocationImage(orgSlug);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const onFile = async (file: File | null) => {
    if (!file) return;
    try {
      await upload.mutateAsync({ locationId: location.id, file });
      setUploadOpen(false);
    } catch {
      /* surfaced below */
    }
  };

  const onSelect = (event: ChangeEvent<HTMLInputElement>) => {
    onFile(event.target.files?.[0] ?? null);
    event.target.value = '';
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    onFile(event.dataTransfer.files[0] ?? null);
  };

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          Image
        </h3>
        {!uploadOpen && (
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="text-xs text-forge-700 dark:text-forge-400 hover:underline"
          >
            {location.image_url ? '+ Replace' : '+ Add image'}
          </button>
        )}
      </div>

      {location.image_url && !uploadOpen && (
        <div className="flex items-start gap-3">
          <a
            href={location.image_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <img
              src={location.image_url}
              alt={location.name}
              className="max-h-[200px] max-w-[200px] rounded-md border border-stone-200 dark:border-stone-800 object-cover"
              loading="lazy"
            />
          </a>
          <button
            type="button"
            onClick={() => {
              if (confirm(`Remove image from ${location.name}?`)) {
                remove.mutate(location.id);
              }
            }}
            disabled={remove.isPending}
            className="text-xs text-stone-500 hover:text-red-600 dark:text-stone-400"
          >
            {remove.isPending ? 'Removing…' : 'Remove'}
          </button>
        </div>
      )}

      {!location.image_url && !uploadOpen && (
        <p className="text-xs text-stone-500 dark:text-stone-400">
          No image yet.
        </p>
      )}

      {uploadOpen && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={cn(
            'flex items-center justify-center gap-2 rounded-md border border-dashed px-4 py-3 text-xs transition-colors',
            dragOver
              ? 'border-forge-400 bg-forge-50 text-forge-700 dark:bg-stone-800 dark:text-forge-400'
              : 'border-stone-300 bg-stone-50 text-stone-500 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-400',
          )}
        >
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            className="text-forge-700 dark:text-forge-400 hover:underline"
          >
            {upload.isPending ? 'Uploading…' : 'Choose a file'}
          </button>
          <span>or drop one here. JPG/PNG/GIF/WebP, up to 5 MB.</span>
          <button
            type="button"
            onClick={() => setUploadOpen(false)}
            className="ml-auto text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200"
            aria-label="Cancel upload"
          >
            ✕
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            onChange={onSelect}
            className="hidden"
          />
        </div>
      )}

      {upload.isError && (
        <p className="mt-2 text-xs text-red-600">
          {(upload.error as Error).message}
        </p>
      )}
      {remove.isError && (
        <p className="mt-2 text-xs text-red-600">
          {(remove.error as Error).message}
        </p>
      )}
    </section>
  );
}

function LocationPartsList({
  orgSlug,
  locationId,
}: {
  orgSlug: string | undefined;
  locationId: number;
}) {
  const parts = useLocationParts(orgSlug, locationId);

  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
        Parts in this location
      </h3>
      {parts.isPending && (
        <p className="text-xs text-stone-500 dark:text-stone-400">Loading…</p>
      )}
      {parts.data && parts.data.length === 0 && (
        <p className="text-xs text-stone-500 dark:text-stone-400">
          No parts assigned to this location.
        </p>
      )}
      {parts.data && parts.data.length > 0 && (
        <div className="overflow-hidden rounded-md border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
          <table className="w-full text-left text-sm">
            <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
              <tr>
                <th className="px-3 py-2">PN</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Form</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              {parts.data.map((row) => (
                <PartRow key={row.part_id} row={row} orgSlug={orgSlug} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PartRow({
  row,
  orgSlug,
}: {
  row: LocationPartRow;
  orgSlug: string | undefined;
}) {
  // Deep-link to /parts?openId=N — PartsPage reads the param on mount,
  // pre-opens the drawer for that part, and seeds the search box with
  // the PN so closing the drawer leaves the row visible in context.
  const href = orgSlug
    ? `/parts?q=${encodeURIComponent(row.part_number)}&openId=${row.part_id}`
    : '#';
  return (
    <tr className="border-t border-stone-100 dark:border-stone-800">
      <td className="px-3 py-2 font-mono">
        <a
          href={href}
          className="text-forge-700 hover:underline dark:text-forge-400"
        >
          {row.part_number}
        </a>
      </td>
      <td className="px-3 py-2 text-stone-700 dark:text-stone-300">
        {row.description ?? row.mfg_pn ?? '—'}
      </td>
      <td className="px-3 py-2 text-stone-600 dark:text-stone-400">
        {formatInventoryForm(row.form)}
      </td>
      <td className="px-3 py-2 text-right font-mono">{row.quantity_on_hand}</td>
      <td className="px-3 py-2 text-right font-mono text-stone-600 dark:text-stone-400">
        {formatCents(row.value_cents)}
      </td>
    </tr>
  );
}
